import { TrashIcon } from "@radix-ui/react-icons"
import { Badge, Box, Button, Card, Flex, Section, Separator, Spinner } from "@radix-ui/themes"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import toast from "react-hot-toast"
import { useMutation } from "react-query"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { Lists } from "../../components/common/Lists"
import TableComponent from "../../components/common/Tables"
import SelectComponent from "../../components/inputs/Select/SelectComponent"
import { useAppDispatch } from "../../hooks/hooks"
import { useGetAssignedTeachersClassesSubjectsQuery } from "../../hooks/useGetAssignedTeachersClassesSubjectsQuery"
import { useGetCoursesSubjectsQuery } from "../../hooks/useGetCoursesSubjectsQuery"
import { useGetTeachersQuery } from "../../hooks/useGetTeachersQuery"
import { RootState } from "../../redux/store"
import { routes } from "../../routes"
import { assignTeacherToSubject } from "../../services/school/teacher/assignTeacherToSubject"
import { updateTeacherClassSubject } from "../../services/school/teacher/removeTeacherClassSubject"
import { useGetPlanSubjectsQuery } from "../../hooks/useGetPlanSubjectsQuery"
import { ClassSubjectTeachersType, TeacherType } from "../../types/Teacher"
import { PagedResultsType } from "../../types/PagedResults"

const columnNamesMap: { [key: string]: string } = {
  teacher: 'Docente',
  subject: 'Disciplina',
};
export const AssignTeacherClassesSubjects = () => {
  const [currentPage, setCurrentPage] = useState(1);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const classData = useSelector((state: RootState) => state.classes);
  const { idCourse, level, semester, idDepartament, id, description, course } = classData;
  const { control, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      idSubject: "",
      idTeacher: ""
    }
  });
  const { data: subjects, isLoading: loadingSubjects, refetch: refreshCourseSubjects } = useGetPlanSubjectsQuery({
    PageSize: 400,
    PageNumber: 1,
    Level: level,
    Semester: semester,
    IdCourse: idCourse,
  });

  const { data: teachersData, isLoading: isloadingTeachersData } = useGetTeachersQuery({
    PageSize: 400,
    PageNumber: 1,
    IdDepartament: idDepartament,
  });

  const { data: teachersClassesSubjectsData, status, isLoading: isLoadingTeachersClassesSubjectsData, refetch: refreshAssignedTeachersClassesSubjects } = useGetAssignedTeachersClassesSubjectsQuery({
    PageSize: 400,
    PageNumber: currentPage,
    IdClass: id,
  });

  const mutation = useMutation(assignTeacherToSubject, {
    onSuccess: () => {
      toast.success("Docente vinculado!");
    },
    onError: () => {
      toast.error("Erro ao vincular docente.");
    }
  });
  const mutationDelete = useMutation(updateTeacherClassSubject, {
    onSuccess: () => {
      toast.success("Vinculo removido!");
    },
    onError: () => {
      toast.error("Erro ao remover associação.");
    }
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      const result = await mutation.mutateAsync({
        idClass: id!,
        idSubject: data.idSubject,
        idTeacher: data.idTeacher,
        status: true
      });
      if (result) {
        refreshAssignedTeachersClassesSubjects();
      }
    } catch (err: any) {
      toast.error(`${mutation.error ?? err?.message}`);
    }
  });
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleDelete = async (classTeacherSubject: any) => {
    classTeacherSubject.status = false;
    await mutationDelete.mutateAsync(classTeacherSubject)
    refreshAssignedTeachersClassesSubjects();

  };
  const options = teachersClassesSubjectsData?.items.map(() => [

    {
      label: 'Excluir',
      onClick: (classTeacherSubject: any) => handleDelete(classTeacherSubject),
      icon: <TrashIcon />
    }

  ]) || [];

  const [sortedTeachersClassesSubjects, setSortedTeachersClassesSubjects] = useState<ClassSubjectTeachersType[] | null>(null);
  const [sortedTeachers, setSortedTeachers] = useState<TeacherType[] | null>(null);

  useEffect(() => {
    if (teachersClassesSubjectsData) {
      const sortedTeachersClassesSubjectsData = [...teachersClassesSubjectsData.items].sort((a, b) =>
        (a.teacher || "").localeCompare(b.teacher || "")
      );
      setSortedTeachersClassesSubjects(sortedTeachersClassesSubjectsData);
    }
  }, [teachersClassesSubjectsData]);  

  useEffect(() => {
    if (teachersData) {
      const sortedTeachersData = [...teachersData.items].sort((a, b) =>
        (a.fullname || "").localeCompare(b.fullname || "")
      );
  
      setSortedTeachers(sortedTeachersData);
    }
  }, [teachersData]);  

  return (
    <Card size='3'>
      <Flex direction="row" gap='6'>
        <Flex direction="column" gap="5" style={{ flex: 1 }}>

          <Flex align="center">
            <Badge variant="surface" >{course}</Badge>
            <Separator orientation="horizontal" />
            <Badge variant="outline" >{description}</Badge>
          </Flex>

          <form onSubmit={onSubmit} noValidate>
            <Flex direction="column" gap='4'>
              <SelectComponent
                label="Disciplina"
                options={subjects?.items.map((subject) => ({
                  value: subject.idSubject,
                  label: subject.subject ?? ""
                }))}
                errors={errors}
                control={control}
                name={"idSubject"}
                required={true}
              />
              <SelectComponent
                label="Docente"
                options={sortedTeachers?.map((teacher) => ({
                  value: teacher.id ?? "",
                  label: `${teacher.fullname} ${teacher.teacherUniversalCode}`
                }))}
                errors={errors}
                control={control}
                name={"idTeacher"}
                required={true}
              />
              <Flex direction="row" gap="4" style={{ alignSelf: "end" }}>
                <Button color="red" onClick={() => navigate(routes.classes)}>
                  Sair
                </Button>
                <Button type="submit" variant="surface" disabled={mutation.isLoading}>
                  {mutation.isLoading ? <Spinner loading /> : "Vincular Docente"}
                </Button>
              </Flex>

            </Flex>
          </form>
        </Flex>
        <Flex direction="column" gap="4" style={{ flex: 1 }}>
          <Lists
            status={status}
            TableComponent={() => <TableComponent
              columns={['teacher', 'subject']}
              items={sortedTeachersClassesSubjects ?? teachersClassesSubjectsData?.items}
              options={options}
              columnNamesMap={columnNamesMap}
              totalPages={teachersClassesSubjectsData?.totalPages || 1}
              currentPage={currentPage}
              onPageChange={handlePageChange} />} handleClickCreate={undefined} />
        </Flex>
      </Flex>
    </Card>
  )
}
